import React, { useCallback, useEffect, useState } from "react";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import {
  Rocket, Loader2, CreditCard, CheckCircle2, XCircle, RefreshCw, Link2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};

const STATUS_META = {
  pending_approval: { label: "Pending approval", cls: "border-amber-400/30 bg-amber-400/10 text-amber-300" },
  approved_awaiting_payment: { label: "Awaiting payment", cls: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300" },
  paid: { label: "Paid — launching", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
  launched: { label: "Launched", cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  launch_failed: { label: "Launch failed", cls: "border-rose-400/30 bg-rose-400/10 text-rose-300" },
  rejected: { label: "Rejected", cls: "border-rose-400/30 bg-rose-400/10 text-rose-300" },
};
const TABS = [
  { key: "pending_approval", label: "Pending" },
  { key: "approved_awaiting_payment", label: "Awaiting payment" },
  { key: "launched", label: "Launched" },
  { key: "launch_failed", label: "Failed" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

export default function LaunchApprovals() {
  const user = auth.getUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending_approval");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/launch-approvals");
      setRows(data.requests || []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load launch requests"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    const chargeMsg = r.total_with_fee <= 0 && r.credit_applied > 0
      ? `Promotional credit (${usd(r.credit_applied)}) covers this launch — no card charge.`
      : `${usd(r.total_with_fee)} will be charged to the requester's card (incl. ${r.cc_fee_pct}% card service fee${r.credit_applied > 0 ? `, after ${usd(r.credit_applied)} promo credit` : ""}).`;
    if (!window.confirm(`Release "${r.campaign_name}"? ${chargeMsg}`)) return;
    setBusy(r.id);
    try {
      const { data } = await api.post(`/launch-approvals/${r.id}/approve`, {});
      if (data.result === "charged_and_launched") {
        toast.success(`Charged ${usd(data.charged)} to ${data.card} — campaign launched 🚀`);
      } else if (data.result === "charged_launch_failed") {
        toast.warning(`Card charged ${usd(data.charged)}, but the ad-platform push failed — use Retry launch`);
      } else if (data.result === "approved_awaiting_payment") {
        toast.success(data.card_error
          ? `Card charge failed (${data.card_error}) — a payment link was sent to the requester`
          : "Approved — a payment link was sent to the requester");
      }
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Approval failed"));
    } finally { setBusy(null); }
  };

  const reject = async (r) => {
    const note = window.prompt(`Decline "${r.campaign_name}"? Add a short reason for the requester:`);
    if (note === null) return;
    setBusy(r.id);
    try {
      await api.post(`/launch-approvals/${r.id}/reject`, { note });
      toast.success("Request declined — the campaign went back to draft");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Reject failed"));
    } finally { setBusy(null); }
  };

  const retryLaunch = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/launch-approvals/${r.id}/retry-launch`);
      if (data.result === "launched") toast.success("Campaign launched 🚀");
      else toast.error(data.launch?.error || "Launch push failed again");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Retry failed"));
    } finally { setBusy(null); }
  };

  const copyLink = async (r) => {
    try {
      const { data } = await api.post(`/launch-approvals/${r.id}/pay`);
      await navigator.clipboard.writeText(data.checkout_url);
      toast.success("Payment link copied");
    } catch (e) {
      toast.error(errMsg(e, "Could not get the payment link"));
    }
  };

  const filtered = tab === "all" ? rows : rows.filter((r) => r.status === tab);
  const counts = rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const isOwnRequest = (r) => r.requested_by_email === user?.email && !["admin", "super_admin", "campaign_manager"].includes(user?.role);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="launch-approvals-page">
      <PageHeader
        eyebrow="Operate"
        title="Launch Approvals"
        subtitle="AI-built campaigns wait here for a human release. Approving charges the requester's card — every credit-card payment includes the platform card service fee."
      />

      <div className="flex gap-2 flex-wrap" data-testid="launch-approvals-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`approvals-tab-${t.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400 hover:border-cyan-400/30"}`}>
            {t.label}{t.key !== "all" && counts[t.key] ? ` (${counts[t.key]})` : ""}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/30 inline-flex items-center gap-1.5" data-testid="approvals-refresh-btn">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading launch requests…</div>
      ) : filtered.length === 0 ? (
        <Card className="!p-8 text-center" data-testid="approvals-empty">
          <Rocket className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <div className="text-sm text-slate-400">No {tab === "all" ? "" : STATUS_META[tab]?.label.toLowerCase() + " "}launch requests right now.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || { label: r.status, cls: "border-white/10 text-slate-300" };
            return (
              <Card key={r.id} className="!p-4" data-testid={`launch-request-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-sm" data-testid="request-campaign-name">{r.campaign_name || "Untitled campaign"}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid={`request-status-${r.id}`}>{meta.label}</span>
                      {r.card_on_file ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-emerald-300 inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Card on file</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400/25 bg-amber-400/5 text-amber-300 inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> No card — link sent on approval</span>
                      )}
                      {r.creative_ready === false && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400/25 bg-amber-400/5 text-amber-300" data-testid={`no-creatives-badge-${r.id}`}>No creatives yet</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      {r.entity_name || "—"} · requested by {r.requested_by_email} · {when(r.created_at)}
                    </div>
                    <div className="text-xs text-slate-300 mt-2 flex gap-4 flex-wrap" data-testid="request-fee-breakdown">
                      <span>Media budget <b>{usd(r.budget)}</b></span>
                      {r.credit_applied > 0 && <span className="text-emerald-300" data-testid={`request-credit-${r.id}`}>Promo credit <b>−{usd(r.credit_applied)}</b></span>}
                      <span>Card fee ({r.cc_fee_pct}%) <b>{usd(r.cc_fee_amount)}</b></span>
                      <span className="text-cyan-300">Total {r.total_with_fee <= 0 && r.credit_applied > 0 ? <b>$0 — covered by credit</b> : <b>{usd(r.total_with_fee)}</b>}</span>
                    </div>
                    {r.status === "rejected" && r.rejection_reason && (
                      <div className="text-[11px] text-rose-300 mt-2">Reason: {r.rejection_reason}</div>
                    )}
                    {r.status === "launch_failed" && r.launch_error && (
                      <div className="text-[11px] text-rose-300 mt-2">Error: {r.launch_error}</div>
                    )}
                    {r.status === "launched" && (
                      <div className="text-[11px] text-emerald-300 mt-2 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Live #{r.sa_campaign_id} · approved by {r.approved_by_email} · {when(r.launched_at)}
                      </div>
                    )}
                    {r.card_error && r.status === "approved_awaiting_payment" && (
                      <div className="text-[11px] text-amber-300 mt-2">Card charge failed: {r.card_error}</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {r.status === "pending_approval" && !isOwnRequest(r) && (
                      <>
                        <Btn onClick={() => approve(r)} disabled={busy === r.id} data-testid={`approve-launch-request-${r.id}`}>
                          {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Approve & release
                        </Btn>
                        <Btn variant="secondary" onClick={() => reject(r)} disabled={busy === r.id} data-testid={`reject-launch-request-${r.id}`}>
                          <XCircle className="w-4 h-4" /> Decline
                        </Btn>
                      </>
                    )}
                    {r.status === "pending_approval" && isOwnRequest(r) && (
                      <div className="text-[11px] text-slate-500 self-center" data-testid="own-request-note">Waiting for an approver</div>
                    )}
                    {r.status === "approved_awaiting_payment" && (
                      <Btn variant="secondary" onClick={() => copyLink(r)} data-testid={`copy-pay-link-${r.id}`}>
                        <Link2 className="w-4 h-4" /> Copy payment link
                      </Btn>
                    )}
                    {r.status === "launch_failed" && !isOwnRequest(r) && (
                      <Btn onClick={() => retryLaunch(r)} disabled={busy === r.id} data-testid={`retry-launch-${r.id}`}>
                        {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Retry launch
                      </Btn>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
